/**
 * PMT — language preference store + gtag/GTM instrumentation.
 *
 * FILENAME: this deliberately does NOT contain the word "analytics". Generic
 * content-blocker filter lists pattern-match that substring in script paths,
 * and this file also owns the preference cookie that decides whether the
 * language prompt is shown — a UI guarantee must not be blockable as tracking.
 * The bar carries its own last-resort fallback too (see hideStage1 in
 * pmt-announcement-bar-v2.js) so the prompt still stops re-appearing if this
 * file never executes.
 *
 * Two jobs, deliberately in one file because they share the same cookie:
 *
 *   1. Remember which language the visitor chose, and HOW we know it
 *      (explicitly chosen vs prompt dismissed). The announcement bar reads
 *      this to decide whether to show Stage 1.
 *
 *   2. Report the choice to GA4 (via gtag) and GTM's dataLayer as a custom
 *      event, without losing it to the page navigation the language switch
 *      triggers.
 *
 * ── Cookie ──────────────────────────────────────────────────────────────
 *   Name  : pmt_lang
 *   Value : <schema>|<lang>|<state>|<yyyymmdd>     e.g. 1|es|sel|20260910
 *             schema  1            value-schema version; append-only
 *             lang    en | es      the language this record is about
 *             state   sel | dis    sel = explicitly chose it
 *                                  dis = dismissed the prompt without choosing
 *             date    UTC yyyymmdd when the decision was made
 *   Attrs : Max-Age=31536000 (365 days); Path=/; SameSite=Lax; Secure on https
 *
 *   `|` is a legal cookie-octet, so the value is stored unencoded and stays
 *   readable in DevTools.
 *
 *   365 IS A REQUEST, NOT A GUARANTEE. What actually lands:
 *     Chrome / Edge / Firefox : 365 days. Chrome's own ceiling is 400 days, so
 *                               we are under it and nothing is truncated.
 *     Safari                  : 7 days. WebKit's ITP caps every cookie written
 *                               through document.cookie in the first-party
 *                               context, with no opt-out. Crucially it CLAMPS
 *                               rather than rejecting — the write succeeds and
 *                               Safari just stores a nearer expiry — so there
 *                               is nothing to detect or handle here. (Nearer
 *                               still, 24 hours, when the visitor arrived via a
 *                               cross-site navigation ITP distrusts.)
 *
 *   Asking for a year is therefore strictly better than asking for a week:
 *   every browser that can honour it does, and Safari gives what it was always
 *   going to give either way. Beating the cap needs a server-set cookie, which
 *   a Liquid theme cannot emit; the durable cross-device answer for signed-in
 *   shoppers is a customer metafield.
 *
 *   The number itself is NOT owned here — see getMaxAgeDays() below.
 *
 * ── Why the event fires on the NEXT page load ───────────────────────────
 *   The language switch is a native form submit, i.e. an immediate navigation.
 *   A dataLayer/gtag call made on click has to survive the unload that
 *   follows it, and that is lossy in proportion to how slow the device is —
 *   which would bias the data against exactly the visitors we most want to
 *   measure.
 *
 *   So a click records its intent in a short-lived session cookie, and the
 *   NEXT page load — which is a fresh document with a full lifetime ahead of
 *   it — fires the event. That also lets the event carry where the visitor
 *   actually LANDED, not merely where they were headed.
 */
(function () {
  "use strict";

  var PREF_COOKIE = "pmt_lang";
  var EVT_COOKIE = "pmt_lang_evt";
  var MIRROR_KEY = "pmt_lang";

  var SCHEMA = "1";
  /* The lifetime is owned by sections/pmt-announcement-bar-v2.liquid, which
     publishes it as data-lang-max-age-days and uses the same number for the
     pre-paint script's mirror check. Reading it from the DOM keeps the cookie,
     the mirror and the bar's fallback writer in lockstep from one Liquid
     assign. This constant is only the fallback for a page that somehow renders
     without the element. */
  var DEFAULT_MAX_AGE_DAYS = 365;

  function getMaxAgeDays() {
    try {
      var el = document.querySelector("[data-lang-max-age-days]");
      if (el) {
        var n = parseFloat(el.getAttribute("data-lang-max-age-days"));
        if (isFinite(n) && n > 0) return n;
      }
    } catch (e) {}
    return DEFAULT_MAX_AGE_DAYS;
  }

  function getMaxAgeSeconds() {
    return Math.round(getMaxAgeDays() * 24 * 60 * 60);
  }

  /* A pending event older than this is stale — the visitor bounced back via
     bfcache, or left the tab open through the switch and came back much later.
     Firing it then would attribute the choice to the wrong session. */
  var PENDING_TTL_MS = 5 * 60 * 1000;

  /* Compared explicitly rather than looked up on an object: `({}).constructor`
     is truthy, so a tampered cookie of `1|es|constructor|20260910` would pass an
     object-lookup validator while the pre-paint regex in the section rejects it,
     leaving the two halves of the contract disagreeing about the same bytes. */
  function isValidState(state) {
    return state === "sel" || state === "dis";
  }

  /* ── Cookie primitives ─────────────────────────────────────────────── */

  function readCookie(name) {
    var prefix = name + "=";
    /* document.cookie THROWS (rather than returning '') in a sandboxed iframe
       without allow-same-origin and under some privacy extensions. Unguarded,
       that SecurityError escapes drain() and becomes an uncaught error on every
       page load. */
    var jar = "";
    try {
      jar = document.cookie || "";
    } catch (e) {
      return null;
    }
    var parts = jar ? jar.split(";") : [];
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i];
      while (c.charAt(0) === " ") c = c.slice(1);
      if (c.indexOf(prefix) === 0) {
        var raw = c.slice(prefix.length);
        try {
          return decodeURIComponent(raw);
        } catch (e) {
          /* A malformed percent-sequence must not throw out of here — an
             earlier version of this code let a URIError abort the whole
             script, which left the announcement bar hidden site-wide. */
          return raw;
        }
      }
    }
    return null;
  }

  function writeCookie(name, value, maxAgeSeconds) {
    var bits = [name + "=" + value, "Path=/", "SameSite=Lax"];
    if (typeof maxAgeSeconds === "number") bits.push("Max-Age=" + maxAgeSeconds);
    if (location.protocol === "https:") bits.push("Secure");
    try {
      document.cookie = bits.join("; ");
    } catch (e) {
      /* cookies blocked — the localStorage mirror is the fallback */
    }
  }

  function deleteCookie(name) {
    try {
      document.cookie = name + "=; Path=/; Max-Age=0; SameSite=Lax";
    } catch (e) {}
  }

  /* ── Preference value ──────────────────────────────────────────────── */

  function utcStamp() {
    var d = new Date();
    var pad = function (n) {
      return (n < 10 ? "0" : "") + n;
    };
    return "" + d.getUTCFullYear() + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate());
  }

  function baseLang(iso) {
    return String(iso || "")
      .split("-")[0]
      .toLowerCase();
  }

  /**
   * Parse and VALIDATE a stored preference. Anything malformed, truncated or
   * tampered with is treated as absent rather than trusted — a bad value must
   * never be able to suppress the prompt.
   */
  function parse(raw) {
    if (!raw) return null;
    var p = String(raw).split("|");
    if (p.length < 4) return null;
    var lang = baseLang(p[1]);
    var state = p[2];
    if (!/^[a-z]{2}$/.test(lang)) return null;
    if (!isValidState(state)) return null;
    if (p[0] !== SCHEMA) return null;
    return { schema: p[0], lang: lang, state: state, date: p[3] };
  }

  /* The mirror exists only so that a visitor whose browser blocks cookies is
     not re-prompted on every single page view. It carries the SAME window as
     the cookie — it is a resilience fallback, not a way to smuggle a longer
     lifetime past the decision above. */
  function writeMirror(value) {
    try {
      localStorage.setItem(MIRROR_KEY, JSON.stringify({ v: value, t: Date.now() }));
    } catch (e) {}
  }

  function readMirror() {
    try {
      var m = JSON.parse(localStorage.getItem(MIRROR_KEY) || "null");
      if (!m || !m.v || !m.t) return null;
      if (Date.now() - m.t > getMaxAgeSeconds() * 1000) return null;
      return m.v;
    } catch (e) {
      return null;
    }
  }

  function read() {
    return parse(readCookie(PREF_COOKIE)) || parse(readMirror());
  }

  function persist(lang, state) {
    var value = [SCHEMA, baseLang(lang), state, utcStamp()].join("|");
    writeCookie(PREF_COOKIE, value, getMaxAgeSeconds());
    writeMirror(value);
    return value;
  }

  /* ── Tracking (gtag / GTM) ─────────────────────────────────────────── */

  /**
   * True inside the Shopify theme editor. Without this, a merchant toggling the
   * header/footer/drawer switcher while checking the bar would write a real
   * preference cookie and emit real gtag/GTM events from the editor iframe —
   * polluting the data during the very QA pass that validates it.
   */
  function isDesignMode() {
    try {
      return !!((window.Shopify && window.Shopify.designMode) || document.querySelector("[data-design-mode]"));
    } catch (e) {
      return false;
    }
  }

  /**
   * Report a language decision to GTM's dataLayer and, when present, GA4
   * directly via gtag. The dataLayer push is unconditional — GTM is already
   * on every page (GTM-5KSBVTD) — and the gtag call is additionally guarded
   * because gtag does not exist until the client finishes the GA4 setup.
   * Event names follow GA4 conventions (snake_case, < 40 chars); one event
   * per action so no trigger config is needed on the GA4 side.
   */
  function trackLanguageEvent(properties) {
    var isDismiss = properties.selection_state === "dis";
    var gtagEventName = isDismiss ? "dismiss_language_prompt" : "select_language";

    /* Debug visibility while testing — remove once verified live. */
    try {
      console.log("[PmtLang] " + gtagEventName, properties);
    } catch (e) {}

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "pmt_language_selected",
        pmt_selected_locale: properties.selected_locale || null,
        pmt_selection_state: properties.selection_state || null,
        pmt_switcher: properties.switcher || null,
      });
    } catch (e) {}

    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", gtagEventName, {
          language: properties.selected_locale || null,
          previous_language: properties.previous_locale || null,
          method: properties.switcher || "unknown",
        });
      } else {
        console.log("[PmtLang] gtag not present yet — event not sent to GA4");
      }
    } catch (e) {}
  }

  function fire(payload) {
    if (!isValidState(payload.selection_state)) return;

    trackLanguageEvent({
      selected_locale: baseLang(payload.selected_locale),
      selection_state: payload.selection_state,
      previous_locale: payload.previous_locale || null,
      /* Captured on THIS page, so they describe where the visitor actually
         ended up rather than where the click intended to send them. */
      landed_locale: document.documentElement.lang || null,
      landed_path: location.pathname,
      switcher: payload.switcher || "unknown",
    });
  }

  /* ── Public API ────────────────────────────────────────────────────── */

  /**
   * Record a language decision.
   *
   * @param {string} lang    'en' | 'es'
   * @param {string} state   'sel' (chose it) | 'dis' (dismissed the prompt)
   * @param {string} source  which control was used, for segmentation
   * @param {object} [opts]  opts.immediate — fire now instead of on the next
   *                         page load. Use ONLY when no navigation follows.
   */
  function record(lang, state, source, opts) {
    if (!isValidState(state)) return;
    if (isDesignMode()) return;
    opts = opts || {};

    /* Clear any marker left by an earlier click whose navigation never
       completed (Stop pressed, another link tapped, /localization 5xx). Without
       this, a stale "chose English" marker would be drained on the next load
       and reported as a switch that never happened. */
    deleteCookie(EVT_COOKIE);

    persist(lang, state);

    var payload = {
      selected_locale: baseLang(lang),
      selection_state: state,
      previous_locale: document.documentElement.lang || null,
      switcher: source || "unknown",
      ts: Date.now(),
    };

    if (opts.immediate) {
      fire(payload);
      return;
    }

    /* Session cookie (no Max-Age): it must not outlive the browser session,
       and drain() consumes it on the very next load. */
    try {
      writeCookie(EVT_COOKIE, encodeURIComponent(JSON.stringify(payload)));
    } catch (e) {}
  }

  function drain() {
    var raw = readCookie(EVT_COOKIE);
    if (!raw) return;

    /* Consume FIRST, so a throw below can never leave a marker that fires the
       same event again on every subsequent page view. */
    deleteCookie(EVT_COOKIE);

    var payload;
    try {
      payload = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!payload || !payload.selected_locale) return;
    if (payload.ts && Date.now() - payload.ts > PENDING_TTL_MS) return;

    fire(payload);
  }

  /* ── The theme's other three language switchers ────────────────────── */

  function sourceForForm(id) {
    if (!id) return "localization_form";
    if (id.indexOf("Footer") === 0) return "footer";
    if (id.indexOf("HeaderLanguageMobile") === 0) return "mobile_drawer";
    if (id.indexOf("Header") === 0) return "header";
    if (id.indexOf("Announcement") === 0) return "announcement_bar_legacy";
    return "localization_form";
  }

  /**
   * The footer, the desktop header and the mobile drawer all render Dawn's
   * <localization-form>. None of them knows about our cookie, so without this
   * a visitor who switches there is re-prompted by the bar on the next page
   * and fires no event at all — an undercount biased by device, since the
   * drawer is mobile-only and the header switcher desktop-only.
   *
   * Capture phase is required: Dawn's own handler is on the bubble phase and
   * ends in form.submit(), so a bubble listener would race the navigation.
   * All we do here is write cookies, which is synchronous and safe.
   */
  document.addEventListener(
    "click",
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var link = target.closest("localization-form a[data-value]");
      if (!link) return;

      var host = link.closest("localization-form");
      var form = host && host.querySelector("form");
      if (!form) return;

      /* Load-bearing guard: header.liquid and footer.liquid render a COUNTRY
         localization-form immediately beside the language one, using
         name="country_code". Without this we would log currency changes as
         language selections. */
      if (!form.querySelector('input[name="locale_code"]')) return;

      var iso = link.getAttribute("data-value");
      if (!iso) return;

      record(iso, "sel", sourceForForm(form.id));
    },
    true,
  );

  window.PmtLang = {
    COOKIE: PREF_COOKIE,
    get MAX_AGE_DAYS() {
      return getMaxAgeDays();
    },
    read: read,
    record: record,
    track: trackLanguageEvent,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", drain);
  } else {
    drain();
  }
})();
